import { Box, Container, Typography } from '@mui/material';
import { Permissions } from '@restore/permission-catalog';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { usePermission } from 'src/hooks/use-permission';
import { useSettings } from 'src/hooks/use-settings';
import { npcLogTreatment } from 'src/config';
import NpcMyEntries from './npc-my-entries';
import NpcReview from './npc-review';

// NPC Log (THIN-7740 / 7742). Anyone who can see the team's entries lands on the facility-wide
// team review dashboard; everyone else (therapists) sees their own entries.
const NpcLog = () => {
  const settings = useSettings();
  const [isNpcEnabled] = useFeatureToggleWhenReady(npcLogTreatment, 'FACILITY');
  // THIN-8173: gate on the scope key via the shared checker, not a hardcoded display name.
  // usePermission reads currentScopes ('APPROVE_NPC') and falls back to the catalog display
  // name — the same dual-emit path every other portal gate uses. The old
  // currentPermissions.includes('Approve NPC Log') check silently failed wherever the profile
  // emitted scopes but not that exact label (or the tenant's permission name had drifted),
  // dropping DORs to the therapist view.
  //
  // THIN-8432: the dashboard is the View Team NPC surface (team list, therapist filter, print),
  // not an Approve-only one — THIN-8327 split the old bundled permission, so a role with View
  // (+ Add/edit) but no Approve (e.g. RSM) must land here too. The dashboard gates its own
  // approve/decline and add/edit/delete actions per permission. APPROVE_NPC stays as a fallback:
  // role saves always pair it with View, so it only matters for a grant made outside that path.
  const canViewTeamNpc = usePermission('VIEW_TEAM_NPC');
  const canApproveNpc = usePermission(Permissions.APPROVE_NPC);
  const canReview = canViewTeamNpc || canApproveNpc;

  // Flag is facility-scoped. THIN-8233: bail until the flag RESOLVES to true — not just when it is
  // explicitly false. While Split is still resolving (null/undefined) the old `=== false` check let
  // the child views mount and fire the NPC fetches against a facility that may have the feature off
  // → 403 → the page blanked behind the route ErrorBoundary. Waiting for `=== true` keeps the views
  // (and their fetches) unmounted until the feature is confirmed enabled.
  if (isNpcEnabled !== true) return null;

  return (
    <Box
      component="main"
      sx={{ flexGrow: 1, py: 4, px: 5 }}
    >
      <Container maxWidth={settings.stretch ? false : 'xl'}>
        <Typography
          variant="h4"
          sx={{ mb: 2 }}
        >
          NPC Log
        </Typography>

        {canReview ? <NpcReview /> : <NpcMyEntries />}
      </Container>
    </Box>
  );
};

export default NpcLog;
